/**
 * Presenza del sito «come WhatsApp Web» (18/10/2026, contratto delle notifiche §6):
 * il sito dice al socket `/chat` se la scheda è visibile e se c'è stata un'interazione.
 * Il server zittisce le push del telefono solo con la scheda visibile e un'interazione
 * negli ultimi 2 minuti (usa la propria ora di ricezione).
 *
 * - `{ visibile: false }` subito quando la scheda si nasconde;
 * - `{ visibile: true, interazione: true }` a un'interazione, al più uno ogni 30 s
 *   (quelle nel mezzo si riassumono in un solo evento alla scadenza);
 * - `{ visibile: true }` quando la scheda torna visibile o il socket si (ri)connette;
 * - alla (ri)connessione, se l'ultima interazione vera (anche a socket chiuso) è dentro la
 *   finestra di 2 minuti del server, `{ visibile: true, interazione: true }`: il server riparte
 *   da zero a ogni socket e chi sta leggendo senza toccare il mouse non deve far suonare il
 *   telefono dopo una caduta di rete (revisione finale, Minor 5 notifiche / 7 sito);
 * - lo scorrimento (`scorrimento()`) conta solo entro 2 s da un input vero: quello automatico
 *   (messaggio arrivato, salto a un messaggio) non tiene «in uso» un PC senza nessuno davanti;
 * - finestra senza fuoco (`sfocato()`): `{ visibile, interazione: false }` subito; il server chiude
 *   la presenza («in uso») senza aspettare i 2 minuti, la visibilità resta com'è. Anche qui
 *   l'interazione recente non vale più (né in attesa, né alla riconnessione) (notifiche fase A, 20/10/2026).
 *
 * Contratto dell'evento `attivita`: `interazione: true` = interazione appena avvenuta;
 * `interazione: false` = presenza finita (blur); campo assente = l'interazione recente resta.
 *
 * Nessun riferimento al DOM: chi lo usa collega gli eventi del browser e il socket.
 */

export const INTERVALLO_ATTIVITA_MS = 30 * 1000;
/** Finestra del server (`FINESTRA_ATTIVITA_WEB_MS`): un'interazione zittisce il telefono per 2 minuti. */
export const FINESTRA_INTERAZIONE_MS = 2 * 60 * 1000;
/** Uno scorrimento conta come interazione solo se un input vero (mouse, tastiera, tocco, rotella) è più recente di così. */
export const SCORRIMENTO_DOPO_INPUT_MS = 2000;

/** `interazione`: true appena avvenuta, false presenza finita (blur), assente nessun cambio. */
export interface DatiAttivita { visibile: boolean; interazione?: boolean }

export interface SegnalatoreAttivita {
  /** Input vero (mouse, tastiera, tocco, rotella, fuoco): ignorato con la scheda nascosta; senza socket solo ricordato. */
  interazione(): void;
  /** Evento `scroll`: conta come interazione solo entro 2 s da un input vero (e non rinnova quei 2 s). */
  scorrimento(): void;
  /** La finestra perde il fuoco: `{ visibile, interazione: false }`, interazione recente dimenticata. */
  sfocato(): void;
  /** Cambio di visibilità della scheda. */
  visibilita(visibile: boolean): void;
  /** Socket (ri)connesso: il server riparte da zero, si manda lo stato attuale (con l'interazione recente). */
  connesso(): void;
  disconnesso(): void;
  /** Fine: nessun altro invio. */
  ferma(): void;
}

export const creaSegnalatoreAttivita = (opzioni: {
  invia: (dati: DatiAttivita) => void;
  visibile: () => boolean;
  intervalloMs?: number;
}): SegnalatoreAttivita => {
  const intervallo = opzioni.intervalloMs ?? INTERVALLO_ATTIVITA_MS;
  let connesso = false;
  let fermo = false;
  /** Ultima visibilità comunicata (null = nulla comunicato su questa connessione). */
  let visibileInviato: boolean | null = null;
  /** Ora dell'ultimo invio con interazione (0 = nessuno: la prossima parte subito). */
  let ultimaInterazioneInviata = 0;
  /** Ora dell'ultima interazione vera a scheda visibile, anche a socket chiuso (0 = nessuna). */
  let ultimaInterazioneReale = 0;
  /** Ora dell'ultimo input vero (non scorrimento): dà validità agli scorrimenti dei 2 s seguenti. */
  let ultimoInputReale = 0;
  /** Interazione arrivata dentro la finestra dei 30 s: parte alla scadenza. */
  let timer: ReturnType<typeof setTimeout> | null = null;

  const annullaTimer = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };

  const inviaInterazione = () => {
    ultimaInterazioneInviata = Date.now();
    visibileInviato = true;
    opzioni.invia({ visibile: true, interazione: true });
  };

  const registraInterazione = () => {
    ultimaInterazioneReale = Date.now();
    if (!connesso) return;
    const attesa = ultimaInterazioneInviata + intervallo - Date.now();
    if (!ultimaInterazioneInviata || attesa <= 0) {
      annullaTimer();
      inviaInterazione();
      return;
    }
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      if (!fermo && connesso && opzioni.visibile()) inviaInterazione();
    }, attesa);
  };

  /** Interazioni dimenticate: nessuna in attesa, la prossima parte subito, nessuna da rimandare alla riconnessione. */
  const dimentica = () => {
    annullaTimer();
    ultimaInterazioneInviata = 0;
    ultimaInterazioneReale = 0;
    ultimoInputReale = 0;
  };

  return {
    interazione() {
      if (fermo || !opzioni.visibile()) return;
      ultimoInputReale = Date.now();
      registraInterazione();
    },
    scorrimento() {
      if (fermo || !opzioni.visibile()) return;
      if (!ultimoInputReale || Date.now() - ultimoInputReale > SCORRIMENTO_DOPO_INPUT_MS) return;
      registraInterazione();
    },
    sfocato() {
      if (fermo) return;
      dimentica();
      if (!connesso) return;
      visibileInviato = opzioni.visibile();
      opzioni.invia({ visibile: visibileInviato, interazione: false });
    },
    visibilita(visibile: boolean) {
      if (fermo) return;
      // la finestra si chiude: niente interazioni in sospeso, la prossima (a scheda visibile) parte subito
      if (!visibile) dimentica();
      if (!connesso || visibileInviato === visibile) return;
      visibileInviato = visibile;
      opzioni.invia({ visibile });
    },
    connesso() {
      if (fermo) return;
      connesso = true;
      annullaTimer();
      ultimaInterazioneInviata = 0;
      visibileInviato = opzioni.visibile();
      if (visibileInviato && ultimaInterazioneReale && Date.now() - ultimaInterazioneReale < FINESTRA_INTERAZIONE_MS) {
        inviaInterazione();
        return;
      }
      opzioni.invia({ visibile: visibileInviato });
    },
    disconnesso() {
      connesso = false;
      visibileInviato = null;
      annullaTimer();
    },
    ferma() {
      fermo = true;
      connesso = false;
      annullaTimer();
    },
  };
};
